import { defineStore } from 'pinia';
import { CUSTOMER_SEGMENTS, DATE_FORMAT_DEFAULT, Store, THEMES_MAP, TIME_FORMAT_DEFAULT } from '@/setup/globals';
import useCourseFavouritesStore from '@/stores/course-favourites';
import { LanguageType } from '@/stores/general';
import { setInitialData } from '@/stores/plugins/initial-data';
import api from '@/helpers/api';

export type StoreState = {
  badgeLevels: Record<string, unknown>[];
  basePath: string;
  cartAmount: number | null;
  dateFormat: string;
  disableCdc: boolean;
  globalLoader: boolean;
  googleMapsApiKey: string;
  hasInvertedSearchSuggestionTabs: boolean;
  isCartEnabled: boolean;
  isLoginEnabled: boolean;
  isMiniPortal: boolean;
  isScreensaverActive: boolean;
  languages: LanguageType[];
  locale: string;
  localPointsColor: string;
  logoUrl: string;
  logoUseWideSize: boolean;
  mainNavigation: Record<string, never>;
  market: string;
  markets: Record<string, unknown>[];
  runningRequests: {
    fetchMarkets: boolean;
    fetchUser: boolean;
    getCartSummary: boolean;
    updatingAccountType: boolean;
    updatingLicenseNumber: boolean;
  };
  screensaverDelay: number;
  services: Record<string, unknown>[];
  showBadgeLevelAchievement: boolean;
  showMarketSelector: boolean;
  startPath: string;
  theme: string;
  timeFormat: string;
  urls: {
    courseListingPage: string;
    eifuLandingpage: string;
    generalTermsOfUsePage: string;
    noSearchResultContactPage: string;
    profileCertificatesPage: string;
    profileFavouriteCoursesPage: string;
    profileUncompletedCoursesPage: string;
    searchResultPage: string;
    serviceLandingPage: string;
  };
  user: Record<string, unknown> | null;
  userNavigation: Record<string, never>[];
};

const storeName = Store.Session;

export default defineStore(storeName, {
  state: () => {
    const initialData: Partial<StoreState> = window.initialData?.[storeName] || {};
    const state: StoreState = {
      badgeLevels: [],
      basePath: '',
      cartAmount: null,
      dateFormat: DATE_FORMAT_DEFAULT,
      disableCdc: false,
      globalLoader: false,
      googleMapsApiKey: '',
      hasInvertedSearchSuggestionTabs: false,
      isCartEnabled: false,
      isLoginEnabled: true,
      isMiniPortal: false,
      isScreensaverActive: false,
      languages: [],
      locale: '',
      localPointsColor: '#005091',
      logoUrl: '',
      logoUseWideSize: false,
      mainNavigation: {},
      market: '',
      markets: [],
      runningRequests: {
        fetchMarkets: false,
        fetchUser: false,
        getCartSummary: false,
        updatingAccountType: false,
        updatingLicenseNumber: false,
      },
      screensaverDelay: 60000, // IDS
      services: [],
      showBadgeLevelAchievement: false,
      showMarketSelector: false,
      startPath: '',
      theme: THEMES_MAP[0],
      timeFormat: TIME_FORMAT_DEFAULT,
      urls: {
        courseListingPage: '',
        eifuLandingpage: '',
        generalTermsOfUsePage: '',
        noSearchResultContactPage: '',
        profileCertificatesPage: '',
        profileFavouriteCoursesPage: '',
        profileUncompletedCoursesPage: '',
        searchResultPage: '',
        serviceLandingPage: '',
      },
      user: null,
      userNavigation: [],
    };

    setInitialData(state, initialData);

    if (state.user) {
      const courseFavouritesStore = useCourseFavouritesStore();

      courseFavouritesStore.favourites = (state.user?.favouriteCourses || []) as number[];
    }

    window.piniaWorkaround = window.piniaWorkaround || {};
    window.piniaWorkaround.basePath = state.basePath;

    return state;
  },
  getters: {
    getUserMarket: (state) => state.user?.market || null,
    getUserIsInternal: (state) => state.user?.internal,
    getIsLoggedIn: (state) => !!state.user?.uid,
    getIsGuestUser: (state) => !state?.user?.uid || state?.user?.segments?.includes(CUSTOMER_SEGMENTS.guest),
    getIsBasicUser: (state) => !!state?.user?.uid && state?.user?.segments?.includes(CUSTOMER_SEGMENTS.basic),
    getIsProfessionalUser: (state) =>
      !!state?.user?.uid && state?.user?.segments?.includes(CUSTOMER_SEGMENTS.professional),
    getIsPendingProfessionalUser: (state) =>
      !!state?.user?.uid && state?.user?.segments?.includes(CUSTOMER_SEGMENTS.professionalPending),
    getBadgeLevelForUser: (state) =>
      !!state.user?.badgeLevelId && state.badgeLevels?.find?.((level) => level.id === state.user?.badgeLevelId),
  },
  actions: {
    /**
     * Gets the cart summary from commerce.
     */
    apiGetCartSummary() {
      if (!this.isCartEnabled) {
        return Promise.resolve(null);
      }

      this.runningRequests.getCartSummary = true;

      const url = api.getUrl('shop-cart-summary');

      return api
        .get(url, null, { preventNotifications: true })
        .then((response) => {
          const { data } = response;

          if (data?.cartTotal >= 0) {
            this.cartAmount = data.cartTotal;

            return response;
          }

          // TODO:
          // dispatch('notification/showUnknownError', null, { root: true });

          throw new Error('API failure during "session/getCartSummary" request.');
        })
        .finally(() => {
          this.runningRequests.getCartSummary = false;
        });
    },

    /**
     * Fetch a list available of markets from the server.
     */
    apiFetchMarkets() {
      this.runningRequests.fetchMarkets = true;

      const url = api.getUrl('market-countries');
      const parameters = {
        url: encodeURIComponent(window.location.pathname),
      };

      return api
        .get(url, { params: parameters })
        .then((response) => {
          const { data } = response.data || {};

          if (data) {
            this.markets = data;

            return data;
          }

          // TODO:
          // dispatch('notification/showUnknownError', null, { root: true });

          throw new Error('API failure during "session/fetchMarkets" request.');
        })
        .finally(() => {
          this.runningRequests.fetchMarkets = false;
        });
    },

    /**
     * Fetch the current users data (refresh initial data with current server state).
     */
    apiFetchUser() {
      this.runningRequests.fetchUser = true;

      const url = api.getUrl('customer-details');

      return api
        .get(url)
        .then((response) => {
          const { data } = response.data || {};

          if (data) {
            this.user = data;

            return data;
          }

          throw new Error('Invalid response for "session/apiFetchUser".');
        })
        .finally(() => {
          this.runningRequests.fetchUser = false;
        });
    },

    /**
     * Updates the users license number in the Pimcore backend.
     */
    updateLicenseNumber(licenseNumber) {
      this.runningRequests.updatingLicenseNumber = true;

      const payload = {
        licenseNumber,
      };
      const url = api.getUrl('customer-update-license-number');

      return api
        .post(url, payload, {}, { preventNotifications: true })
        .then(() => {
          this.user.licenseNumber = licenseNumber;

          return licenseNumber;
        })
        .finally(() => {
          this.runningRequests.updatingLicenseNumber = false;
        });
    },

    /**
     * Updates the users account type in the Pimcore backend.
     */
    updateAccountType(accountType) {
      this.runningRequests.updatingAccountType = true;

      const payload = {
        accountType,
      };
      const url = api.getUrl('customer-update-account-type');

      return api
        .post(url, payload, {}, { preventNotifications: true })
        .then(() => {
          return accountType;
        })
        .finally(() => {
          this.runningRequests.updatingAccountType = false;
        });
    },
  },
});
